import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "antd";
import VoiceCaptureSetupChecklist from "./VoiceCaptureSetupChecklist";
import { CHANNEL_TYPES, WIDGET_STATUS } from "./constants";
import {
  saveVoiceWidgetChannels,
  setVoiceWidgetStatus,
} from "./voiceService";
import styles from "./style.module.scss";

const WidgetSetupDrawer = ({
  open,
  widget,
  apiReady,
  onClose,
  onWidgetUpdated,
}) => {
  const { t } = useTranslation();
  const [activating, setActivating] = useState(false);
  const [enablingEmbed, setEnablingEmbed] = useState(false);

  const linkEnabled = useMemo(
    () =>
      (widget?.channels || []).some(
        (c) => c.channel_type === CHANNEL_TYPES.LINK_QR && c.enabled,
      ),
    [widget],
  );

  const handleActivate = useCallback(async () => {
    if (!widget) return;
    setActivating(true);
    try {
      const updated = await setVoiceWidgetStatus(widget.id, WIDGET_STATUS.ACTIVE);
      onWidgetUpdated(updated);
    } finally {
      setActivating(false);
    }
  }, [widget, onWidgetUpdated]);

  const handleEnableEmbed = useCallback(async () => {
    if (!widget) return;
    setEnablingEmbed(true);
    try {
      const embedCh = (widget.channels || []).find(
        (c) => c.channel_type === CHANNEL_TYPES.WEB_EMBED,
      );
      const updated = await saveVoiceWidgetChannels(widget.id, [
        {
          channel_type: CHANNEL_TYPES.LINK_QR,
          enabled: linkEnabled,
          config: {},
        },
        {
          channel_type: CHANNEL_TYPES.WEB_EMBED,
          enabled: true,
          config: { display_mode: embedCh?.config?.display_mode || "popup" },
        },
      ]);
      onWidgetUpdated(updated);
    } finally {
      setEnablingEmbed(false);
    }
  }, [widget, linkEnabled, onWidgetUpdated]);

  return (
    <Drawer
      title={
        widget
          ? t("voiceCaptureManagement.setup_drawer_title", { name: widget.name })
          : t("voiceCaptureManagement.setup_widget_title")
      }
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
      className={styles.setupDrawer}
    >
      {widget ? (
        <VoiceCaptureSetupChecklist
          widget={widget}
          apiReady={apiReady}
          showProgressHeader
          onActivate={handleActivate}
          onEnableEmbed={handleEnableEmbed}
          activating={activating}
          enablingEmbed={enablingEmbed}
        />
      ) : null}
    </Drawer>
  );
};

export default WidgetSetupDrawer;
